import ButtonLoader from "@components/ButtonLoader/ButtonLoader";
import CardStripe from "@components/CardStripe/CardStripe";
import { onProcessItemType } from "@store/OnProcessPayment";
import { rootStore } from "@store/Route";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { AlertError, AlertSuccess } from "@utils/alert";
import { formatPrice } from "@utils/format";
import { delay } from "@utils/helper";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import AddCreditCard from "./components/AddCreditCard";
const { REACT_APP_CLIENT_SECRET } = process.env;

const stripePromise = loadStripe(REACT_APP_CLIENT_SECRET || "");

interface ICardSelection extends ICard {
  isSelected: boolean;
}

interface ParamTypes {
  url?: string;
}

const SummaryStripe = observer(() => {
  // url stripe for onproccessing
  const { url } = useParams<ParamTypes>();
  const { outstandingPayment, payment, landing, onProcess } = rootStore;
  const { totalBill, selectedItemsInvoice, setSelectItemsInvoice } =
    outstandingPayment;
  const { token } = landing;
  const {
    getListPaymentMethods,
    paymentMethods,
    statusAttachCreditCard,
    attachCreditCard,
    initialPayment,
    fetchingRequestPaymentMethod,
    resetNewPayment,
    requestPaymentMethod,
    getRequestPaymentMehods,
    getCheckBackCharge,
    checkBankCharge,
  } = payment;
  const { getItemInvoiceProcessing } = onProcess;
  let history = useHistory();

  const [isShowCreditCard, setIsShowCreditCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState<ICardSelection | null>(null);
  const [itemInvoiceProcessing, setItemInvoiceProcessing] =
    useState<onProcessItemType | null>();

  const getData = useCallback(() => {
    if (token) {
      getListPaymentMethods("stripe", token, async () => {
        getRequestPaymentMehods("stripe", token);
        if (!url) {
          getCheckBackCharge("stripe", token, totalBill || 0);
        }
      });
    }
  }, [token, url, totalBill]);

  useEffect(() => {
    initialPayment();
    getData();
    resetNewPayment();
  }, []);

  /* This is a useEffect hook that is called when the url changes. */
  useEffect(() => {
    if (url) {
      setItemInvoiceProcessing(getItemInvoiceProcessing(url));
    }
  }, [url]);

  useEffect(() => {
    if (token && itemInvoiceProcessing?.amount) {
      getCheckBackCharge("stripe", token, itemInvoiceProcessing.amount);
    }
  }, [itemInvoiceProcessing]);

  useEffect(() => {
    if (paymentMethods && paymentMethods.length !== 0) {
      const findIndex = paymentMethods.findIndex(
        (item) => item.isDefault === true
      );
      setSelectedCard({ ...paymentMethods[findIndex], isSelected: true });
    }
  }, [paymentMethods]);

  useEffect(() => {
    if (statusAttachCreditCard === 200 && attachCreditCard) {
      handelAttachCreditCard();
    }
  }, [statusAttachCreditCard && attachCreditCard]);

  const handelAttachCreditCard = async () => {
    setIsShowCreditCard(false);
    await delay(500);
    getData();
    initialPayment();
  };

  const invoiceType = (type: string) => {
    if (type === "invoice") {
      return "Invoice";
    }
    if (type === "credit_note") {
      return "Credit Note";
    }
    return "";
  };

  return (
    <div>
      <div className="w-4/5 m-0 mx-auto py-28 h-full relative flex md:flex-row flex-col">
        <div className="md:w-1/3 w-full space-y-5">
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold text-white">
              Payment Summary
            </h1>
            <h1 className="text-5xl font-bold text-white">
              {`${formatPrice(totalBill || 0)}`}
            </h1>
          </div>
          <div className="space-y-2">
            {checkBankCharge?.bankCharges && totalBill ? (
              <>
                <h1 className="text-3xl font-semibold text-white">Admin Fee</h1>
                <h1 className="text-5xl font-bold text-white">
                  {`${formatPrice(checkBankCharge.bankCharges - totalBill)}`}
                </h1>
              </>
            ) : null}
          </div>
          {selectedItemsInvoice.map((item) => (
            <div key={item.accountingDocumentNumber} className="mt-6">
              <p className="text-3xl text-white">{`${invoiceType(
                item.type || ""
              )} ${item.accountingDocumentNumber}`}</p>
            </div>
          ))}
        </div>
        <div className="bg-white md:w-2/3 w-full rounded-lg p-10 md:mt-0 mt-10">
          {fetchingRequestPaymentMethod === "pending" ||
          !requestPaymentMethod?.clientSecret ? (
            <div className="loaderWrapper">
              <div className="preloader preloaderBus" />
            </div>
          ) : (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret: requestPaymentMethod.clientSecret }}
            >
              {isShowCreditCard ? (
                <AddCreditCard />
              ) : (
                <div>
                  {paymentMethods && paymentMethods.length !== 0 ? (
                    paymentMethods.map((item) => (
                      <div
                        key={item.paymentMethodId}
                        className="mb-6 cursor-pointer"
                        onClick={() =>
                          setSelectedCard({ ...item, isSelected: true })
                        }
                      >
                        <CardStripe
                          isSelected={
                            item.paymentMethodId ===
                            selectedCard?.paymentMethodId
                              ? selectedCard?.isSelected
                              : false
                          }
                          item={item}
                        ></CardStripe>
                      </div>
                    ))
                  ) : (
                    <div className="ChildItem__mainWrapper">
                      <div className="row no-gutters">
                        <p className="offset-3 col-6 text-center header--16 color--blue">
                          You haven't any Credit Card.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedCard && !isShowCreditCard ? (
                <PayStripe
                  selectedCard={selectedCard}
                  setSelectedCard={setSelectedCard}
                  url={url}
                ></PayStripe>
              ) : null}

              <div
                className="mt-6"
                onClick={() => setIsShowCreditCard(!isShowCreditCard)}
              >
                <ButtonLoader secondary className=" w-full">
                  {isShowCreditCard ? "Cancel" : "Add New Credit Card"}
                </ButtonLoader>
              </div>

              {isShowCreditCard ? null : (
                <p className="mt-6 text-red-500 text-center">
                  Transition Fees are wholly paid to the Payment Gateway
                  provider, Stripe. Ling Wei Transport does not receive any
                  transaction fee.
                </p>
              )}

              {paymentMethods && paymentMethods.length !== 0 ? (
                <div
                  onClick={() => {
                    history.push("/dashboard/manage/credit-card");
                  }}
                  className="mt-6 cursor-pointer text-blue-900"
                >
                  <h3 className="underline underline-offset-2">
                    Manage Your Credit Card Here
                  </h3>
                </div>
              ) : null}
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
});

interface PayStripeProps {
  selectedCard: ICardSelection;
  setSelectedCard: (card: ICardSelection) => void;
  url?: string;
}

const PayStripe = observer(
  ({ selectedCard, setSelectedCard, url }: PayStripeProps) => {
    const { outstandingPayment, payment, landing } = rootStore;
    const { totalBill, selectedItemsInvoice, setSelectItemsInvoice } =
      outstandingPayment;
    const { token } = landing;
    const {
      fetchingRequestPaymentMethod,
      createNewPayment,
      setActiveInvoiceTabSection,
      statusCreatePayment,
      createPayment,
    } = payment;
    const stripe = useStripe();
    let history = useHistory();
    const [isLoading, setIsLoading] = useState(false);

    const backToTabPayment = (tab: string) => {
      history.push("/dashboard/payment");
      setIsLoading(false);
      setActiveInvoiceTabSection(tab);
      setSelectItemsInvoice([]);
    };

    const handelConfrimPayment = async (clientSecret: string) => {
      if (stripe && clientSecret) {
        const result = await stripe.confirmCardPayment(clientSecret, {});
        if (result.paymentIntent?.status === "succeeded") {
          AlertSuccess(
            "Your payment has been processed",
            "Please check processing tab for more information"
          );
        } else {
          AlertError(
            "Error Payment",
            `${
              result.error?.message || "Your payment cannot proccess"
            }. Please check processing tab for continue your payment`
          );
        }
      } else {
        AlertError(
          "Error Payment",
          "Stripe not found.\n Please check processing tab for more continue your payment"
        );
      }
      backToTabPayment("1");
      setIsLoading(false);
    };

    const onPay = async () => {
      if (url) {
        handelConfrimPayment(url);
      } else if (
        selectedItemsInvoice.length !== 0 &&
        token &&
        selectedCard?.paymentMethodId
      ) {
        await createNewPayment(
          token,
          selectedItemsInvoice,
          "stripe",
          selectedCard.paymentMethodId,
          async (callback) => {}
        );
      } else {
        AlertError("Cannot Process Payment", "Please check your Invoice");
        backToTabPayment("0");
        setIsLoading(false);
      }
    };

    useEffect(() => {
      if (statusCreatePayment === 200 && createPayment?.url) {
        handelConfrimPayment(createPayment.url);
      }
    }, [statusCreatePayment, createPayment]);

    return (
      <div>
        <ButtonLoader
          isLoading={isLoading}
          onClick={() => {
            setIsLoading(true);
            onPay();
          }}
          className=" w-full"
        >
          Pay
        </ButtonLoader>
      </div>
    );
  }
);

export default SummaryStripe;
